import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { supabase } from "../services/supabaseClient";
import { safeMarkdown } from "../utils/sanitize";
import { handleWechatLinkClick } from "../utils/wechatLink";
import { useLang } from "../i18n/context";
import RebateBanner from "../components/RebateBanner";
import docs from "../data/docs";
import "./DocsPage.css";

// ── SVG 图标 ──────────────────────────────
const FileTextIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
    <rect x="6" y="3" width="12" height="18" rx="2" fill="rgba(212,168,67,0.15)" />
    <path d="M9 7h6M9 11h6M9 15h3" stroke="var(--accent-primary)" strokeWidth="2" />
  </svg>
);

const BookIcon = () => (
  <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="var(--accent-primary)" strokeWidth="2" strokeLinecap="round" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" fill="rgba(77,163,255,0.12)" stroke="var(--accent-primary)" strokeWidth="2" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── 工具函数 ──────────────────────────────
const BEGINNER_CATEGORY = "新手教学";
const BEGINNER_CATEGORY_ALIASES = new Set([
  "入门指南",
  "操作教程",
  "入门",
  "名词解释",
  "新手知识",
  BEGINNER_CATEGORY,
]);

const normalizeDocCategory = (category) => {
  const value = (category || "").trim();
  if (!value) return BEGINNER_CATEGORY;
  return BEGINNER_CATEGORY_ALIASES.has(value) ? BEGINNER_CATEGORY : value;
};

const getSummary = (content) =>
  content
    .replace(/[#*>`[\]!()|]/g, "")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\n+/g, " ")
    .trim()
    .slice(0, 100);

// ── 文档内容组件（含灯箱） ────────────────
function DocsContent({ doc }) {
  if (!doc) return null;

  const html = safeMarkdown(doc.content);

  const handleClick = (e) => {
    handleWechatLinkClick(e);

    if (e.target.tagName !== "IMG") return;

    const container = e.currentTarget;
    const images = Array.from(container.querySelectorAll("img"));
    const index = images.indexOf(e.target);
    if (index === -1) return;

    const showImage = (i) => {
      const imgEl = document.getElementById("docs-lightbox-img");
      const counter = document.getElementById("docs-lightbox-counter");
      if (imgEl) imgEl.src = images[i].src;
      if (counter)
        counter.textContent =
          images.length > 1 ? `${i + 1} / ${images.length}` : "";
      document.getElementById("docs-lightbox-prev").style.display =
        i > 0 ? "block" : "none";
      document.getElementById("docs-lightbox-next").style.display =
        i < images.length - 1 ? "block" : "none";
      document.getElementById("docs-lightbox-img").dataset.index = i;
    };

    Swal.fire({
      html: `
        <div style="position:relative;display:flex;align-items:center;justify-content:center;height:80vh;overflow:hidden">
          <button id="docs-lightbox-prev" style="position:absolute;left:0;top:50%;transform:translateY(-50%);z-index:2;background:rgba(212,168,67,0.15);border:none;color:#d4a843;font-size:2rem;padding:0.5rem 1rem;cursor:pointer;border-radius:8px;display:${index > 0 ? "block" : "none"}">&#8249;</button>
          <div id="docs-lightbox-wrap" style="overflow:hidden;max-width:100%;max-height:100%;display:flex;align-items:center;justify-content:center;position:relative;width:100%;height:100%">
            <img id="docs-lightbox-img" src="${images[index].src}" data-index="${index}" data-scale="1" draggable="false" style="max-width:100%;max-height:100%;object-fit:contain;border-radius:8px;transition:none;cursor:grab;user-select:none" />
          </div>
          <button id="docs-lightbox-next" style="position:absolute;right:0;top:50%;transform:translateY(-50%);z-index:2;background:rgba(212,168,67,0.15);border:none;color:#d4a843;font-size:2rem;padding:0.5rem 1rem;cursor:pointer;border-radius:8px;display:${index < images.length - 1 ? "block" : "none"}">&#8250;</button>
          <div id="docs-lightbox-counter" style="position:absolute;bottom:-30px;color:rgba(255,255,255,0.5);font-size:0.85rem">${images.length > 1 ? `${index + 1} / ${images.length}` : ""}</div>
          <div id="docs-lightbox-hint" style="position:absolute;top:-25px;color:rgba(255,255,255,0.3);font-size:0.75rem">滚轮缩放 · 拖拽移动 · 双击还原</div>
        </div>
      `,
      background: "transparent",
      backdrop: "rgba(0,0,0,0.85)",
      showCloseButton: true,
      showConfirmButton: false,
      width: "90%",
      didOpen: () => {
        const img = document.getElementById("docs-lightbox-img");
        let scale = 1;
        let tx = 0, ty = 0;
        let dragging = false, startX, startY;

        const applyTransform = () => {
          img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
        };

        const resetView = () => {
          scale = 1; tx = 0; ty = 0;
          applyTransform();
        };

        img.addEventListener("wheel", (e) => {
          e.preventDefault();
          const delta = e.deltaY > 0 ? 0.9 : 1.1;
          scale = Math.max(0.3, Math.min(5, scale * delta));
          applyTransform();
        });

        img.addEventListener("mousedown", (e) => {
          if (scale <= 1) return;
          dragging = true;
          startX = e.clientX - tx;
          startY = e.clientY - ty;
          img.style.cursor = "grabbing";
        });
        document.addEventListener("mousemove", (e) => {
          if (!dragging) return;
          tx = e.clientX - startX;
          ty = e.clientY - startY;
          applyTransform();
        });
        document.addEventListener("mouseup", () => {
          dragging = false;
          img.style.cursor = scale > 1 ? "grab" : "zoom-in";
        });

        img.addEventListener("dblclick", () => resetView());

        const showImageWithReset = (i) => {
          showImage(i);
          scale = 1; tx = 0; ty = 0;
          applyTransform();
        };

        document.getElementById("docs-lightbox-prev").onclick = () => {
          const cur = parseInt(img.dataset.index);
          if (cur > 0) showImageWithReset(cur - 1);
        };
        document.getElementById("docs-lightbox-next").onclick = () => {
          const cur = parseInt(img.dataset.index);
          if (cur < images.length - 1) showImageWithReset(cur + 1);
        };

        let lastTouchDist = 0;
        let touchDragging = false;
        let touchStartX = 0, touchStartY = 0;
        img.addEventListener("touchstart", (e) => {
          if (e.touches.length === 2) {
            e.preventDefault();
            touchDragging = true;
            lastTouchDist = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY,
            );
            touchStartX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - tx;
            touchStartY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - ty;
          } else if (e.touches.length === 1 && scale > 1) {
            touchDragging = true;
            touchStartX = e.touches[0].clientX - tx;
            touchStartY = e.touches[0].clientY - ty;
          }
        }, { passive: false });
        img.addEventListener("touchmove", (e) => {
          if (!touchDragging) return;
          e.preventDefault();
          if (e.touches.length === 2) {
            const dist = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY,
            );
            if (lastTouchDist > 0) {
              scale = Math.max(0.3, Math.min(5, scale * (dist / lastTouchDist)));
            }
            lastTouchDist = dist;
            tx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - touchStartX;
            ty = (e.touches[0].clientY + e.touches[1].clientY) / 2 - touchStartY;
            applyTransform();
          } else if (e.touches.length === 1 && scale > 1) {
            tx = e.touches[0].clientX - touchStartX;
            ty = e.touches[0].clientY - touchStartY;
            applyTransform();
          }
        }, { passive: false });
        img.addEventListener("touchend", (e) => {
          if (e.touches.length === 0) {
            touchDragging = false;
            lastTouchDist = 0;
          }
        });
      },
    });
  };

  return (
    <div className="docs-content" onClick={handleClick}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

// ── 主页面组件 ──────────────────────────────
function DocsPage() {
  const navigate = useNavigate();
  const { docId } = useParams();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('cat') || undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [apiDocs, setApiDocs] = useState(null);       // null = not loaded yet
  const [detailDoc, setDetailDoc] = useState(null);   // single doc with full content
  const [newbieGuides, setNewbieGuides] = useState([]);
  const docsPageSize = 10;

  // Fetch articles from API (fallback to local data)
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await fetch('/api/articles/');
        if (res.ok) {
          const data = await res.json();
          if (data.articles && data.articles.length > 0) {
            setApiDocs(data.articles.map((a) => ({
              id: a.slug,
              title: a.title,
              category: normalizeDocCategory(a.category),
              content: a.content || '',
              summary: a.summary || '',
              cover_image: a.cover_image || '',
            })));
            return;
          }
        }
      } catch { /* fallback below */ }
      setApiDocs(null);
    };
    fetchArticles();
  }, []);

  // Fetch newbie_guides as fallback supplement
  useEffect(() => {
    if (apiDocs !== null) return; // API succeeded, skip newbie_guides
    const fetchGuides = async () => {
      try {
        const { data, error } = await supabase
          .from('newbie_guides')
          .select('id, title, content, category, cover_image, created_at')
          .order('created_at', { ascending: false });
        if (!error && data) {
          setNewbieGuides(data.map(g => ({
            id: `guide-${g.id}`,
            title: g.title,
            category: normalizeDocCategory(g.category),
            content: g.content || '',
            cover_image: g.cover_image,
            isGuide: true,
          })));
        }
      } catch {}
    };
    fetchGuides();
  }, [apiDocs]);

  // Fetch single article detail by slug (for detail view from API)
  useEffect(() => {
    if (!docId) { setDetailDoc(null); return; }
    // If we already have content in allDocs, no need to fetch
    const existing = (apiDocs || []).find((d) => d.id === docId);
    if (existing && existing.content) { setDetailDoc(null); return; }
    // Fetch full article from API
    const fetchDetail = async () => {
      try {
        const res = await fetch(`/api/articles/?slug=${encodeURIComponent(docId)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.article) {
            setDetailDoc({
              id: data.article.slug,
              title: data.article.title,
              category: normalizeDocCategory(data.article.category),
              content: data.article.content || '',
            });
          }
        }
      } catch {}
    };
    fetchDetail();
  }, [docId, apiDocs]);

  const fallbackDocs = [...docs, ...newbieGuides].map((doc) => ({
    ...doc,
    category: normalizeDocCategory(doc.category),
  }));

  const allDocs = apiDocs !== null ? apiDocs : fallbackDocs;
  const selectedDoc = docId
    ? detailDoc || allDocs.find((d) => d.id === docId)
    : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [docId]);

  const categories = [...new Set(allDocs.map((d) => d.category))];
  const filteredDocs = activeCategory
    ? allDocs.filter((d) => d.category === activeCategory)
    : allDocs;
  const shouldPaginate = !activeCategory && filteredDocs.length > docsPageSize;
  const totalPages = shouldPaginate ? Math.ceil(filteredDocs.length / docsPageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const visibleDocs = shouldPaginate
    ? filteredDocs.slice((safePage - 1) * docsPageSize, safePage * docsPageSize)
    : filteredDocs;
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

  const changeCategory = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  const changePage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── 视图 B：文档详情 ────────────────────
  if (docId && selectedDoc) {
    const sameCategoryDocs = allDocs.filter((d) => d.category === selectedDoc.category);
    const currentIndex = sameCategoryDocs.findIndex((d) => d.id === selectedDoc.id);
    const prevDoc = currentIndex > 0 ? sameCategoryDocs[currentIndex - 1] : null;
    const nextDoc = currentIndex < sameCategoryDocs.length - 1 ? sameCategoryDocs[currentIndex + 1] : null;

    return (
      <div className="docs-page docs-detail-page">
        <div className="docs-detail-body">
          <div className="docs-detail-wrap">
            <button
              className="docs-back-link"
              onClick={() => navigate("/docs")}
            >
              <ArrowLeftIcon /> {t("docs.backToList")}
            </button>

            <div className="docs-detail-meta">
              <span className="docs-detail-category">
                {selectedDoc.category}
              </span>
            </div>

            <h1 className="docs-detail-title">{selectedDoc.title}</h1>

            <article className="docs-detail-article">
              <DocsContent doc={selectedDoc} />
            </article>

            {/* 上下篇导航 */}
            <div className="docs-nav-row">
              {prevDoc ? (
                <button
                  className="docs-nav-btn prev"
                  onClick={() => navigate(`/docs/${prevDoc.id}`)}
                >
                  <span className="docs-nav-label">{t("docs.prevDoc")}</span>
                  <span className="docs-nav-title">{prevDoc.title}</span>
                </button>
              ) : (
                <div className="docs-nav-spacer" />
              )}
              {nextDoc ? (
                <button
                  className="docs-nav-btn next"
                  onClick={() => navigate(`/docs/${nextDoc.id}`)}
                >
                  <span className="docs-nav-label">{t("docs.nextDoc")}</span>
                  <span className="docs-nav-title">{nextDoc.title}</span>
                </button>
              ) : (
                <div className="docs-nav-spacer" />
              )}
            </div>

            <RebateBanner />
          </div>
        </div>
      </div>
    );
  }

  // ── 视图 A：文档列表 ────────────────────
  return (
    <div className="docs-page">
      {/* Banner */}
      <div className="docs-banner">
        <h1 className="docs-banner-title">{t("docs.bannerTitle")}</h1>
        <p className="docs-banner-subtitle">{t("docs.bannerDesc")}</p>
        <div className="docs-nav-bar">
          <div className="docs-nav-line" />
          <div className="docs-nav-tabs">
            <button
              className={`docs-nav-tab${!activeCategory ? " active" : ""}`}
              onClick={() => changeCategory(undefined)}
            >
              {t("docs.all")}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`docs-nav-tab${activeCategory === cat ? " active" : ""}`}
                onClick={() => changeCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="docs-nav-line" />
        </div>
      </div>

      {/* 主体 */}
      <main className="docs-main">
        <div className="docs-container">
          {/* 书籍库入口横幅 */}
          <div className="docs-books-banner" onClick={() => navigate('/books')}>
            <BookIcon />
            <div className="docs-books-banner-text">
              <strong>📚 书籍库</strong>
              <span>精选投资经典，提炼核心精华 →</span>
            </div>
          </div>

          {/* 卡片网格 */}
          <div className="docs-grid">
            {visibleDocs.map((doc) => (
              <div
                key={doc.id}
                className="docs-card"
                onClick={() => navigate(`/docs/${doc.id}`)}
              >
                <div className="docs-card-header">
                  <div className="docs-card-icon-wrap">
                    <FileTextIcon />
                  </div>
                  <span className="docs-card-arrow">
                    <ChevronRightIcon />
                  </span>
                </div>
                <h3 className="docs-card-title">{doc.title}</h3>
                <p className="docs-card-summary">
                  {getSummary(doc.content)}
                </p>
                <div className="docs-card-footer">
                  <span className="docs-card-tag">{doc.category}</span>
                </div>
              </div>
            ))}
          </div>

          {shouldPaginate && (
            <div className="docs-pagination" role="navigation" aria-label={t("docs.pagination")}>
              <button
                className="docs-page-btn"
                onClick={() => changePage(safePage - 1)}
                disabled={safePage === 1}
              >
                {t("docs.prevPage")}
              </button>
              <div className="docs-page-numbers">
                {pageNumbers.map((page) => (
                  <button
                    key={page}
                    className={`docs-page-number${page === safePage ? " active" : ""}`}
                    onClick={() => changePage(page)}
                    aria-current={page === safePage ? 'page' : undefined}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                className="docs-page-btn"
                onClick={() => changePage(safePage + 1)}
                disabled={safePage === totalPages}
              >
                {t("docs.nextPage")}
              </button>
            </div>
          )}

          <RebateBanner />
        </div>
      </main>
    </div>
  );
}

export default DocsPage;
